/* The primitive position type, and nothing else yet.
 *
 * This exists because `snap.ts` was importing `LatLon` from
 * `$signalk/resources` — a SignalK REST client — for a two-field record that
 * has nothing to do with SignalK. That import was the only thing stopping the
 * snapping maths from being portable, and a chart that cannot be built without
 * a SignalK server is not the chart this package is trying to be.
 *
 * The type stays re-exported from `signalk/resources` in the app, so the many
 * call sites that reach for it there did not have to move.
 */

/** Degrees, WGS84. Longitude is signed, west negative — the convention every
 *  dataset the chart consumes already uses, and the one GeoJSON requires. */
export interface LatLon {
  lat: number;
  lon: number;
}

/**
 * A lat/lon rectangle.
 *
 * The field NAMES are deliberate: `WindFieldBounds` and the NEMO lattice header
 * already spell it this way, and MapLibre's `LngLatBounds` answers
 * `getWest()`/`getNorth()`. Anything that reads "minLon/maxLat" would need
 * translating at every one of those boundaries.
 *
 * ANTIMERIDIAN: not handled, and that is a decision rather than an oversight.
 * Every dataset this chart consumes is Canadian or US Pacific, so a box that
 * wraps 180° cannot arise; the day one can, `bboxIntersects` is where it goes,
 * and it should refuse rather than quietly answering for the wrong half of the
 * world.
 */
export interface Bbox {
  west: number;
  south: number;
  east: number;
  north: number;
}

/** Is a point inside a box? Points on the boundary count as covered. */
export function pointInBbox(box: Bbox, lon: number, lat: number): boolean {
  return lon >= box.west && lon <= box.east && lat >= box.south && lat <= box.north;
}

/** Do two boxes share any area? Touching edges count — a layer whose data
 *  starts exactly at the edge of the view does have data in the view. */
export function bboxIntersects(a: Bbox, b: Bbox): boolean {
  return a.west <= b.east && b.west <= a.east && a.south <= b.north && b.south <= a.north;
}

/** The smallest box containing every point, or null for none. */
export function bboxOf(points: readonly LatLon[]): Bbox | null {
  if (points.length === 0) return null;
  let west = Infinity;
  let south = Infinity;
  let east = -Infinity;
  let north = -Infinity;
  for (const p of points) {
    if (!Number.isFinite(p.lat) || !Number.isFinite(p.lon)) continue;
    if (p.lon < west) west = p.lon;
    if (p.lon > east) east = p.lon;
    if (p.lat < south) south = p.lat;
    if (p.lat > north) north = p.lat;
  }
  return Number.isFinite(west) ? { west, south, east, north } : null;
}

/**
 * Grow a box by `deg` on every side, clamped to the world.
 *
 * Coverage derived from station positions is the box the STATIONS sit in, which
 * is not the box they are useful in: the outermost tide station on a coast is
 * still the one you want when you are ten miles offshore of it, and a hairline
 * box would grey the layer out exactly there. A degree is about 60 nautical
 * miles, which is the scale at which "there is no data for this area" starts
 * being true rather than pedantic.
 */
export function padBbox(b: Bbox, deg: number): Bbox {
  return {
    west: Math.max(-180, b.west - deg),
    east: Math.min(180, b.east + deg),
    south: Math.max(-90, b.south - deg),
    north: Math.min(90, b.north + deg)
  };
}

/**
 * A route point that may BE a waypoint resource that already exists.
 *
 * Measured on this boat: two of three stored routes bind every point this way.
 * See `mergedProperties` below for why the binding must be tracked per point
 * rather than inferred from the array's length.
 */
export interface BoundPoint extends LatLon {
  /** Waypoint resource id this point IS, when it is one. */
  wptId?: string;
  /** The name AS LAST KNOWN FROM THE SERVER. Display baseline — see `pointName`. */
  wptName?: string;
  /**
   * A name typed in THIS editing session, not yet saved. M5.3.
   *
   * Absent means "leave whatever this point already is/becomes alone" —
   * `ensureRouteWaypoints` reads it against `wptName` to decide whether a
   * bound point needs renaming, and uses it as the new waypoint's name when
   * the point has none yet. Cleared back to undefined once a save adopts it,
   * the same reconciliation `wptId` itself goes through in M17.2.
   */
  pointName?: string;
  /**
   * The point's original `coordinatesMeta` entry, carried whole.
   *
   * We understand `href` and nothing else in there, and rebuilding the entry
   * from scratch would silently drop whatever another tool had put beside it.
   * Carrying the object means unknown fields travel with the point and are
   * discarded at exactly the moment the binding is — the only moment they stop
   * applying.
   */
  meta?: Record<string, unknown>;
}
